import {useContext, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {Button} from '@/components/app/button';
import {ChoiceSelect} from '@/components/app/choice-select';
import {LoadingSkeleton} from '@/components/app/feedback';
import {Hierarchy} from '@/components/app/hierarchy';
import {cn} from '@/lib/utils';
import {Icon} from '../../../app/components/Icon';
import {SearchBar} from '../../../app/components/SearchBar';
import {TextMatch} from '../../../app/components/TextMatch';
import {useGetConditionsQuery, useGetConditionTypesQuery} from '../../../app/services/conditionApi';
import {PersonContext} from '../../person/contexts/PersonContext';
import {Condition, ConditionTypeName} from '../types';

// synthetic condition for grouping conditions that do not have a parent and are not groups
const othersCondition: Condition = {
  conditionName: 'Others',
  conditionTypeName: '' as ConditionTypeName,
  id: -1,
  isChildSelectionRestricted: false,
  isGroup: true,
  otherParentLabelIds: [],
  parentLabelId: null,
  showOrder: Number.MAX_SAFE_INTEGER,
  synonymNames: [],
};
interface Group {
  id: number;
  name: string;
  showOrder: number | null;
}
interface TreeNode {
  children: TreeNode[];
  data: {
    ascendents: Condition[];
    condition: Condition;
    selection: 'single' | 'multiple';
  };
  key: number;
  label: string;
  leaf: boolean;
}
interface ConditionSelectionPanelProps {
  className?: string;
  conditionTypeName: string;
  onSave: (event: {deselected: Condition[]; selected: Condition[]}) => void;
  onSelectGroup?: (event: {group: Group}) => void;
}
export const ConditionSelectionPanel = ({
  className,
  conditionTypeName,
  onSave,
  onSelectGroup,
}: ConditionSelectionPanelProps) => {
  const {isRequiredRelated, isSelected, relevantConditionsDeduplicated, userId} = useContext(PersonContext);
  const [selectedGroupId, setSelectedGroupId] = useState<number>();
  const [willBeDeselectedConditionIds, setWillBeDeselectedConditionIds] = useState<number[]>([]);
  const [willBeSelectedConditionIds, setWillBeSelectedConditionIds] = useState<number[]>([]);
  const {data: conditionTypes} = useGetConditionTypesQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const conditionType = conditionTypes?.find((conditionType) => conditionType.conditionTypeName === conditionTypeName);
  const {data: allConditions = [], isLoading: isLoadingGetConditions} = useGetConditionsQuery(
    {
      conditionTypeId: conditionType?.id,
      includeGroups: true,
      pageNumber: 1,
      pageSize: 1000,
      searchTerm: '',
      userId,
    },
    {
      skip: conditionType == null,
    },
  );
  const visibleConditions = useMemo(() => {
    const othersVisible = allConditions?.some((c) => c.parentLabelId == null && !c.isGroup);
    const conditions = othersVisible ? [...allConditions, othersCondition] : allConditions;
    return conditions.filter((c) => isVisible(c)) ?? [];
  }, [allConditions, searchQuery]);
  const groups: Group[] = useMemo(
    () =>
      visibleConditions
        .filter((condition) => condition.parentLabelId == null && condition.isGroup)
        .map((condition) => createGroup(condition))
        .sort(compareGroups),
    [visibleConditions],
  );
  const treeNodes = useMemo(() => createTreeNodes(visibleConditions), [visibleConditions]);
  const treeValue = treeValueFactory(treeNodes, selectedGroupId);
  const treeExpandedKeys = useMemo(
    () => Object.fromEntries(allConditions?.map((rc) => [rc.id, true].concat([othersCondition.id, true])) ?? []),
    [allConditions],
  );
  useEffect(() => {
    setWillBeDeselectedConditionIds([]);
    setWillBeSelectedConditionIds([]);
  }, [relevantConditionsDeduplicated]);
  const changesExist = hasChanges();

  // handles auto-selection on search
  useLayoutEffect(() => {
    if (searchQuery !== '') {
      let newSelectedGroupId: number | undefined = selectedGroupId;
      if (selectedGroupId == null) {
        if (groups.length > 0) {
          newSelectedGroupId = groups[0].id;
        }
      } else {
        if (!groups.some((group) => group.id === selectedGroupId)) {
          newSelectedGroupId = undefined;
        }
      }
      if (newSelectedGroupId !== selectedGroupId) {
        setSelectedGroupId(newSelectedGroupId);
      }
    }
  }, [groups, searchQuery, selectedGroupId]);
  if (isLoadingGetConditions) {
    return (
      <div className={cn('flex flex-col bg-gray-100 sm:rounded-xl', className)}>
        <div className="flex items-center p-6">
          <div className="text-blue-400">Add {conditionTypeName} Features</div>
        </div>
        <div className={'flex flex-col lg:flex-row flex-1 min-h-0 px-6 pb-6'}>
          <LoadingSkeleton className={'lg:hidden rounded-full mb-4'} height={'3rem'} width={'100%'} />
          <div className={'hidden lg:block flex-1 min-w-0'}>
            <div className={'flex flex-col gap-4 pr-6'}>
              <LoadingSkeleton height="1rem" width="5rem" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="5rem" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="100%" />
              <LoadingSkeleton height="1rem" width="100%" />
            </div>
          </div>
          <div className={'flex-[2] min-h-fit min-w-0'}>
            <LoadingSkeleton className={'h-[12rem] lg:h-full rounded-xl'} height="100%" width="100%" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={cn('flex flex-col bg-gray-100 sm:rounded-xl', className)}>
      <div
        className="flex items-center p-6"
        style={
          changesExist
            ? {
                background: 'inherit',
                borderRadius: 'inherit',
                position: 'sticky',
                top: '2.25rem',
                zIndex: 1,
              }
            : {}
        }>
        <div className="flex-1 min-w-0">
          <div className="text-blue-400 whitespace-nowrap">Add {conditionTypeName} Features</div>
        </div>
        <div className="flex-[2] flex items-center min-w-0">
          <div
            className={'flex-1 flex items-center'}
            style={{
              height: 0,
            }}>
            <SearchBar
              className={'w-full ml-auto'}
              onChange={(e) => {
                setSearchQuery(e.value);
                if (!e.value) {
                  setSelectedGroupId(undefined);
                }
              }}
              placeholder={'Search'}
              value={searchQuery}
            />
          </div>
          {changesExist && (
            <div
              className="flex items-center ml-2"
              style={{
                height: 0,
              }}>
              <Button
                onClick={() => cancelChanges()}
                type={'button'}
                variant="ghost"
                className="rounded-full"
                startIcon={<Icon icon={'times'} />}>
                {'Cancel Changes'}
              </Button>
              <Button
                onClick={() => saveChanges()}
                type={'button'}
                variant="success-ghost"
                className="rounded-full"
                startIcon={<Icon icon={'save'} />}>
                {'Save Changes'}
              </Button>
            </div>
          )}
        </div>
      </div>
      <div className={'lg:flex flex-1 min-h-0 px-6 pb-6'}>
        <ChoiceSelect
          className={'lg:hidden w-full mb-4'}
          onValueChange={(e) => {
            const group = groups.find((c) => c.id === e);
            if (group != null) {
              selectGroup(group);
            }
          }}
          options={groups.map((group) => ({
            label: group.name,
            value: group.id,
          }))}
          placeholder={'Select a group'}
          value={selectedGroupId}
        />
        <div className={'hidden lg:block flex-1 min-w-0 overflow-auto mb-4'}>
          <section>
            <header>
              <h6 className={'text-base font-semibold my-0 py-2 pr-4'}>
                {conditionTypeName} {conditionTypeName === ConditionTypeName.FamilyHistory ? 'of ...' : ''}
              </h6>
            </header>
            <div>
              {groups.map((group) => {
                const selected = selectedGroupId === group.id;
                return (
                  <div
                    className={cn(
                      'cursor-pointer px-4 py-2 rounded-l-xl',
                      selected ? 'bg-white text-primary' : 'hover:bg-gray-200',
                    )}
                    onClick={() => selectGroup(group)}
                    onKeyDown={(e) => e.key === 'Enter' && selectGroup(group)}
                    key={group.id}
                    role={'button'}
                    tabIndex={0}>
                    <TextMatch matcher={searchQuery} text={group.name} />
                  </div>
                );
              })}
            </div>
          </section>
        </div>
        <div className={'flex-[2] min-w-0'}>
          <div className={'p-4 overflow-auto bg-white rounded-xl h-full'}>
            {treeValue.length > 0 && (
              <Hierarchy
                expanded={treeExpandedKeys}
                renderNode={(node) => {
                  if (node.data == null) {
                    return null;
                  }
                  const condition = node.data.condition as Condition;
                  if (condition.isGroup) {
                    return <h6 className={'p-2 font-medium text-base my-0'}>{condition.conditionName}</h6>;
                  }
                  const requiredRelated = isRequiredRelated(condition.id);
                  const selected = isSelected(condition.id);
                  const selectedDescendentIds = getSelectedDescendentIds(condition);
                  const willBeDeselected = willBeDeselectedConditionIds.includes(condition.id);
                  const willBeDeselectedDescendentIds = getWillBeDeselectedDescendentIds(condition);
                  const willBeSelected = willBeSelectedConditionIds.includes(condition.id);
                  const willBeSelectedDescendentIds = getWillBeSelectedDescendentIds(condition);
                  const willBeChanged = willBeDeselected || willBeSelected;
                  const willBeEmpty =
                    !selected &&
                    selectedDescendentIds.length > 0 &&
                    selectedDescendentIds.length === willBeDeselectedDescendentIds.length &&
                    willBeSelectedDescendentIds.length === 0;
                  const disabled =
                    requiredRelated ||
                    (selectedDescendentIds.length > 0 &&
                      willBeDeselectedDescendentIds.length < selectedDescendentIds.length) ||
                    willBeSelectedDescendentIds.length > 0;
                  const selection = node.data.selection;
                  return (
                    <div
                      aria-disabled={disabled}
                      className={cn('flex items-center gap-2 w-full p-2 rounded-xl', {
                        'opacity-50': disabled,
                        'cursor-pointer hover:bg-gray-200': !disabled,
                        'text-yellow-800': willBeChanged || willBeEmpty,
                      })}
                      onClick={() => !disabled && toggleConditionSelection(condition)}
                      onKeyDown={(e) => e.key === 'Enter' && !disabled && toggleConditionSelection(condition)}
                      role={'button'}
                      tabIndex={0}>
                      <Icon
                        className={'block'}
                        icon={
                          willBeDeselected || (!willBeSelected && willBeEmpty)
                            ? selection === 'multiple'
                              ? 'stop'
                              : 'circle'
                            : willBeSelected || willBeSelectedDescendentIds.length > 0
                              ? selection === 'multiple'
                                ? 'check-square'
                                : 'check-circle'
                              : requiredRelated || selected || selectedDescendentIds.length > 0
                                ? selection === 'multiple'
                                  ? 'check-square'
                                  : 'check-circle'
                                : selection === 'multiple'
                                  ? 'stop'
                                  : 'circle'
                        }
                        style={{
                          marginTop: -1,
                        }}
                      />
                      <div className={'flex-1 min-w-0'}>
                        <TextMatch matcher={searchQuery} text={condition.conditionName} />
                      </div>
                    </div>
                  );
                }}
                nodes={treeValue}
              />
            )}
            {selectedGroupId != null && treeValue.length === 0 && (
              <div className={'text-muted-foreground p-2'}>No features available.</div>
            )}
            {selectedGroupId == null && searchQuery === '' && (
              <div className={'text-muted-foreground p-2'}>Please select a group to list features.</div>
            )}
            {selectedGroupId == null && searchQuery !== '' && (
              <div className={'text-muted-foreground p-2'}>No results found.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  function isVisible(condition: Condition): boolean {
    if (isMatched(condition)) {
      return true;
    }
    if (getChildList(condition).some((child) => isVisible(child))) {
      return true;
    }
    if (getAscendents(condition).some((ascendent) => isMatched(ascendent))) {
      return true;
    }
    return false;
  }
  function isMatched(condition: Condition): boolean {
    return condition.conditionName.toLowerCase().includes(searchQuery.toLowerCase());
  }
  function selectGroup(group: Group) {
    setSelectedGroupId(group.id);
    onSelectGroup?.({
      group,
    });
  }
  function treeValueFactory(nodes: TreeNode[], selectedGroupId: number | undefined) {
    if (selectedGroupId == null) {
      return [];
    }
    return nodes.find((node) => node.data.condition.id === selectedGroupId)?.children ?? [];
  }
  function cancelChanges() {
    setWillBeDeselectedConditionIds([]);
    setWillBeSelectedConditionIds([]);
  }
  function hasChanges() {
    return willBeDeselectedConditionIds.length > 0 || willBeSelectedConditionIds.length > 0;
  }
  function saveChanges() {
    onSave({
      deselected: allConditions?.filter((condition) => willBeDeselectedConditionIds.includes(condition.id)) ?? [],
      selected: allConditions?.filter((condition) => willBeSelectedConditionIds.includes(condition.id)) ?? [],
    });
  }
  function getSelectedDescendentIds(condition: Condition): number[] {
    const childList = getChildList(condition);
    return childList.flatMap((child) => (isSelected(child.id) ? child.id : getSelectedDescendentIds(child)));
  }
  function getWillBeDeselectedDescendentIds(condition: Condition): number[] {
    const childList = getChildList(condition);
    return childList.flatMap((child) =>
      willBeDeselectedConditionIds.includes(child.id) ? child.id : getWillBeDeselectedDescendentIds(child),
    );
  }
  function getWillBeSelectedDescendentIds(condition: Condition): number[] {
    const childList = getChildList(condition);
    return childList.flatMap((child) =>
      willBeSelectedConditionIds.includes(child.id) ? child.id : getWillBeSelectedDescendentIds(child),
    );
  }
  function toggleConditionSelection(condition: Condition) {
    const selected = isSelected(condition.id);
    const requiredRelated = isRequiredRelated(condition.id);
    const willBeSelected = willBeSelectedConditionIds.includes(condition.id);
    const willBeDeselected = willBeDeselectedConditionIds.includes(condition.id);
    const ascendents = getAscendents(condition);
    const ascendentIds = ascendents.map((c) => c.id) ?? [];
    if (selected || requiredRelated) {
      if (willBeDeselected) {
        setWillBeDeselectedConditionIds((ids) => ids.filter((id) => id !== condition.id));
      } else {
        setWillBeDeselectedConditionIds((ids) => [...ids, condition.id]);
      }
    }
    if (!selected && !requiredRelated) {
      if (willBeSelected) {
        setWillBeSelectedConditionIds((ids) => ids.filter((id) => id !== condition.id));
      } else {
        setWillBeSelectedConditionIds((ids) => [...ids.filter((id) => !ascendentIds.includes(id)), condition.id]);
      }
    }
  }
  function getAscendents(condition: Condition): Condition[] {
    const ascendents: Condition[] = [];
    let current = condition;
    let parent = getParent(condition);
    while (parent != null) {
      ascendents.push(parent);
      current = parent;
      parent = getParent(current);
    }
    return ascendents;
  }
  function getChildList(condition: Condition): Condition[] {
    if (condition === othersCondition) {
      return allConditions?.filter((c) => c.parentLabelId == null && !c.isGroup) ?? [];
    }
    return (
      allConditions?.filter((c) => c.parentLabelId === condition.id || c.otherParentLabelIds?.includes(condition.id)) ??
      []
    );
  }
  function getParent(condition: Condition): Condition | undefined {
    if (condition.parentLabelId == null && condition.otherParentLabelIds.length === 0) {
      // return undefined if the condition is a root condition
      if (condition.isGroup) {
        return;
      }

      // return synthetic parent condition if the condition is not a group
      return othersCondition;
    }
    return allConditions?.find(
      (c) => c.id === condition.parentLabelId || condition.otherParentLabelIds?.includes(c.id),
    );
  }
  function createGroup(condition: Condition): Group {
    return {
      id: condition.id,
      name: formatGroupName(condition.conditionName),
      showOrder: condition.showOrder,
    };
  }
  function createTreeNodes(conditions: Condition[]): TreeNode[] {
    return conditions
      .filter((condition) => condition.parentLabelId == null && condition.isGroup)
      .sort(compareConditions)
      .map((c) => createTreeNode(c, conditions));
  }
  function createTreeNode(condition: Condition, conditions: Condition[]): TreeNode {
    const childList =
      condition === othersCondition
        ? conditions.filter((c) => c.parentLabelId == null && !c.isGroup)
        : conditions.filter((c) => c.parentLabelId === condition.id || c.otherParentLabelIds?.includes(condition.id));
    const ascendents = getAscendents(condition);
    return {
      children: childList
        .slice()
        .sort(compareConditions)
        .map((child) => createTreeNode(child, conditions)),
      data: {
        ascendents,
        condition,
        selection: ascendents.some((a) => a.isChildSelectionRestricted) ? 'single' : 'multiple',
      },
      key: condition.id,
      label: condition.conditionName,
      leaf: childList.length === 0,
    };
  }
};
function compareConditions(a: Condition, b: Condition) {
  return (
    Number(a.showOrder ?? Number.MAX_SAFE_INTEGER) - Number(b.showOrder ?? Number.MAX_SAFE_INTEGER) ||
    a.conditionName.localeCompare(b.conditionName)
  );
}
function compareGroups(a: Group, b: Group) {
  return (
    Number(a.showOrder ?? Number.MAX_SAFE_INTEGER) - Number(b.showOrder ?? Number.MAX_SAFE_INTEGER) ||
    a.name.localeCompare(b.name)
  );
}
function formatGroupName(groupName: string) {
  return groupName.replace(/Family history( of|,) (\w)/g, (c) => c.slice(-1).toUpperCase());
}
